import { useEffect, useState } from "react";
import api from "@/lib/api";
import CrudPage from "@/components/CrudPage";
import { useAuth, canWrite } from "@/context/AuthContext";
import { rupiah, num } from "@/lib/format";

export default function EnvironmentalCost() {
  const { user } = useAuth();
  const [batches, setBatches] = useState(null);

  useEffect(() => {
    api.get("/batches").then((b) => setBatches(b.data)).catch(() => setBatches([]));
  }, []);

  if (!batches) return <div className="text-slate-400 py-20 text-center">Memuat...</div>;

  const emis = (r) => r.emisi_material + r.emisi_listrik + r.emisi_bbm + r.emisi_air;
  const total = (r) =>
    r.limbah_padat_vol * r.limbah_padat_tarif +
    r.limbah_cair_vol * r.limbah_cair_tarif +
    r.energi_lingkungan +
    emis(r) / 1000 * r.carbon_tarif +
    r.pengendalian -
    r.reuse_recycle_saving;

  return (
    <CrudPage
      title="Environmental Cost"
      subtitle="Modul biaya lingkungan: limbah, energi, emisi karbon, carbon cost, reuse/recycle, pengendalian."
      endpoint="/environmental-costs"
      canWrite={canWrite(user?.role, ["environmental_officer"])}
      scope={{ key: "batch_id", placeholder: "Pilih Batch", options: batches.map((b) => ({ value: b.id, label: `${b.no_batch} — ${b.product_name}` })) }}
      columns={[
        { key: "limbah_padat", label: "Limbah Padat", align: "right", render: (r) => rupiah(r.limbah_padat_vol * r.limbah_padat_tarif) },
        { key: "limbah_cair", label: "Limbah Cair", align: "right", render: (r) => rupiah(r.limbah_cair_vol * r.limbah_cair_tarif) },
        { key: "energi", label: "Energi Lingk.", align: "right", render: (r) => rupiah(r.energi_lingkungan) },
        { key: "emisi", label: "Emisi", align: "right", render: (r) => `${num(emis(r), 1)} kg` },
        { key: "carbon", label: "Carbon Cost", align: "right", render: (r) => rupiah(emis(r) / 1000 * r.carbon_tarif) },
        { key: "reuse", label: "Hemat Reuse", align: "right", render: (r) => "- " + rupiah(r.reuse_recycle_saving) },
        { key: "total", label: "Total Lingkungan", align: "right", render: (r) => <span className="font-semibold text-green-700">{rupiah(total(r))}</span> },
      ]}
      fields={[
        { key: "limbah_padat_vol", label: "Volume Limbah Padat (ton)", type: "number", step: "0.01" },
        { key: "limbah_padat_tarif", label: "Tarif Limbah Padat (Rp/ton)", type: "number" },
        { key: "limbah_cair_vol", label: "Volume Limbah Cair (m³)", type: "number", step: "0.01" },
        { key: "limbah_cair_tarif", label: "Tarif Limbah Cair (Rp/m³)", type: "number" },
        { key: "energi_lingkungan", label: "Biaya Energi Lingkungan (Rp)", type: "number" },
        { key: "emisi_material", label: "Emisi Material (kg CO₂)", type: "number", step: "0.1" },
        { key: "emisi_listrik", label: "Emisi Listrik (kg CO₂)", type: "number", step: "0.1" },
        { key: "emisi_bbm", label: "Emisi BBM (kg CO₂)", type: "number", step: "0.1" },
        { key: "emisi_air", label: "Emisi Air (kg CO₂)", type: "number", step: "0.1" },
        { key: "carbon_tarif", label: "Tarif Karbon (Rp/ton CO₂e)", type: "number" },
        { key: "reuse_recycle_saving", label: "Penghematan Reuse/Recycle (Rp)", type: "number" },
        { key: "pengendalian", label: "Biaya Pengendalian Lingkungan (Rp)", type: "number" },
      ]}
    />
  );
}
